import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { TrophyIcon, XIcon } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';

const SESSION_KEY = 'challengeWeeklySummaryChecked';

function WeeklyToast({ t, count, joined, onCta }) {
  return (
    <div className="relative w-[calc(100vw-2rem)] max-w-md">
      <div className="relative flex flex-wrap items-center gap-3 rounded-xl sm:flex-nowrap border border-[#e9b54e] bg-[var(--q3-surface-default)] p-4 shadow-lg">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#f7d98b] to-[#e9b54e]">
          <TrophyIcon size={22} weight="fill" className="text-[#2a1a05]" />
        </span>
        <div className="min-w-0 flex-1 pr-5 sm:pr-0">
          <p className="text-sm font-semibold text-[var(--q3-text-default)]">
            <b className="text-[#e9b54e]">{count.toLocaleString('en-IN')}</b>{' '}
            {joined
              ? 'students are climbing the leaderboard this week'
              : 'students are already in the 99 Percentile Challenge'}
          </p>
          <p className="text-xs text-[var(--q3-text-subdued,inherit)] opacity-80">
            {joined
              ? "Don't fall behind - keep your streak alive."
              : 'Join now and start climbing the weekly leaderboard.'}
          </p>
        </div>
        <Button
          size="sm"
          className="w-full shrink-0 border-0 bg-gradient-to-r from-[#f7d98b] to-[#e9b54e] font-semibold text-[#2a1a05] hover:opacity-90 sm:w-auto"
          onClick={() => onCta(t.id)}
        >
          {joined ? 'Keep going' : 'Join now'}
        </Button>
        <button
          type="button"
          aria-label="Close"
          onClick={() => toast.remove(t.id)}
          className="absolute right-2 top-2 shrink-0 opacity-70 sm:static sm:opacity-100"
        >
          <XIcon />
        </button>
      </div>
    </div>
  );
}

// The backend consumes the day's toast on the call that returns show:true (once per IST day), so
// this must render whenever it gets show:true - the sessionStorage guard only avoids re-calling per route.
export function ChallengeWeeklyToast() {
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_KEY)) return;
      // set before the call so StrictMode's double effect (or a remount) can't send a second request -
      // the backend consumes the day's toast on the first show:true, so the second would get show:false
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      // storage blocked: fall through, the server still caps it at once a day
    }
    axios
      .get('/api/challenge/weekly-summary')
      .then((res) => {
        const d = res?.data?.data;
        if (!d?.show) return;
        toast.custom(
          (t) => (
            <WeeklyToast
              t={t}
              count={d.completedCount}
              joined={d.joined}
              onCta={(id) => {
                toast.remove(id);
                navigate('/challenge');
              }}
            />
          ),
          { position: window.innerWidth < 800 ? 'top-center' : 'bottom-center', duration: 10000 },
        );
      })
      .catch(() => {
        try {
          sessionStorage.removeItem(SESSION_KEY); // failed call consumed nothing, allow a retry
        } catch {
          // storage blocked: nothing to undo
        }
      });
  }, [navigate]);

  return null;
}
